import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { useWelcomePopupStore } from '#modules/user/features/onboarding/model/welcomePopupStore';
import { tracking } from '#features/analytics';
import {
  isReviewPromptEnabled,
  isUpdatePromptVisible,
} from '#features/app-update/utils/statusFlags';
import { useCallStore } from '#features/calls/model/callStore';
import { usePushOnboardingStore } from '#features/notifications/model/pushOnboardingStore';
import { pendingSessionSurveyQueryOptions } from '#features/sessionSurvey/model/sessionSurvey';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import {
  NEW_VISIT_AFTER_MS,
  REVIEW_LAST_ASKED_KEY,
  REVIEW_RETRY_DELAYS_MS,
  REVIEW_VISIT_DELAY_MS,
} from '../constants';
import { clearEarnedOutcome, readEarnedOutcome } from '../model/earnedOutcome';
import { isPostNudgeVisible } from '../model/screenFlags';
import { canAskForReview, parseLastAskedAt } from '../utils/reviewGate';
import { requestStoreReview } from '../utils/storeReview';

/**
 * Занят ли экран разговором, который начали до нас.
 *
 * Снимок, а не подписка: вопрос имеет смысл ровно в момент попытки. Всё, что здесь
 * перечислено, человек не откладывал — он либо в звонке, либо ему что-то показали, — и
 * карточка Play легла бы поверх системным окном, которое не подвинуть и не закрыть из кода.
 */
const isScreenBusy = (surveyPending: boolean): boolean => {
  if (isUpdatePromptVisible()) {
    return true;
  }

  if (usePushOnboardingStore.getState().visible) {
    return true;
  }

  if (useWelcomePopupStore.getState().open) {
    return true;
  }

  // Открытый чат — это разговор с живым человеком, и он же наш главный повод спросить. Ронять
  // карточку в середину переписки значило бы испортить ровно тот опыт, за который просим.
  // Лент бывает несколько (окно комнаты сессии поверх страницы) – занята любая из них.
  if (useActiveChatStore.getState().hasOpenChat()) {
    return true;
  }

  // Окно «назначь сбор» встречает человека сразу после созданной заявки.
  if (isPostNudgeVisible()) {
    return true;
  }

  const { phase } = useCallStore.getState();

  // `elsewhere` тоже занимает экран: там висит предложение перенести звонок сюда.
  if (phase !== 'idle' && phase !== 'ended') {
    return true;
  }

  // Опрос «сыграли ли» — единственный случай, где дело не только во внимании. Вопрос о
  // впечатлении прямо перед карточкой оценки — это то, что политика Play запрещает прямым
  // текстом, независимо от того, читаем мы ответ или нет.
  if (surveyPending) {
    return true;
  }

  return envConfig.IN_BROWSER && document.visibilityState === 'hidden';
};

/**
 * Просит Play показать карточку оценки — в заход, следующий за тем, в котором человек получил
 * от продукта результат.
 *
 * Повод отмечают чужие места (`noteOutcome` в ветке чата и в ответе на опрос), решение
 * принимается здесь: только тут видно, свободен ли экран. Разделение не косметическое —
 * ни чат, ни опрос не знают, что поверх них висит шторка разрешений или идёт звонок.
 *
 * Отметка о попытке ставится ДО обращения к Play и только когда просьба действительно уходит.
 * Play на свой отказ по квоте отвечает успехом и ничего не рисует, поэтому единственная защита
 * от бесконечных попыток — собственный таймер. А вот занятый экран заработанное право не жжёт:
 * отметка остаётся, и следующий заход попробует снова.
 *
 * В вебе не делает ничего: карточка существует только в приложении Play.
 */
export const useStoreReviewPrompt = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let visitStartedAt = Date.now();
    let pausedAt: number | null = null;
    let timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const attempt = () => {
      if (cancelled) {
        return;
      }

      const earned = readEarnedOutcome();

      if (!earned) {
        return;
      }

      // Кеш, а не запрос: сам запрос на сервере считается показом карточки опроса, и дёргать
      // его ради проверки занятости значило бы сжигать чужой лимит показов.
      const survey = queryClient.getQueryData(pendingSessionSurveyQueryOptions(true).queryKey);

      const allowed = canAskForReview({
        now: Date.now(),
        earnedAt: earned.at,
        visitStartedAt,
        lastAskedAt: parseLastAskedAt(localStorage.getItem(REVIEW_LAST_ASKED_KEY)),
        enabled: isReviewPromptEnabled(),
        busy: isScreenBusy(Boolean(survey)),
      });

      if (!allowed) {
        return;
      }

      localStorage.setItem(REVIEW_LAST_ASKED_KEY, String(Date.now()));
      clearEarnedOutcome();

      // Play не сообщает ни того, показал ли он карточку, ни того, что человек поставил.
      // Поэтому единственное, что вообще можно сопоставить с отзывами в консоли, — наш
      // собственный след: когда попросили и за что.
      tracking.event({
        action: 'store_review_requested',
        category: 'store-review',
        label: earned.reason,
      });

      void requestStoreReview();
    };

    const scheduleVisit = () => {
      timers.forEach(clearTimeout);
      timers = [REVIEW_VISIT_DELAY_MS, ...REVIEW_RETRY_DELAYS_MS].map((delay) =>
        setTimeout(attempt, delay)
      );
    };

    scheduleVisit();

    let remove: (() => void) | undefined;

    void (async () => {
      try {
        const { App } = await import('@capacitor/app');

        // Холодный старт в приложении — редкость: WebView живёт сутками, и без этого слушателя
        // человек, вернувшийся назавтра, не был бы спрошен вовсе.
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            pausedAt = Date.now();

            return;
          }

          if (pausedAt !== null && Date.now() - pausedAt >= NEW_VISIT_AFTER_MS) {
            visitStartedAt = Date.now();
            scheduleVisit();
          }

          pausedAt = null;
        });

        if (cancelled) {
          void handle.remove();
        } else {
          remove = () => void handle.remove();
        }
      } catch {
        // @capacitor/app недоступен — остаётся попытка от запуска, этого достаточно
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      remove?.();
    };
  }, [queryClient]);
};
